import {Component} from 'vue-property-decorator';
import {PageInfo, TableHeader} from '@/model/CommonModel';
import DefaultViewMixin from '@/views/DefaultViewMixin';
import ApiUtils from '@/utils/ApiUtils';
import MessageUtils from '@/utils/MessageUtils';
import CommonUtils from '@/utils/CommonUtils';

/**
 * 모든 Navigation 기반 Paginated Table이 존재하는 View에서 적용해야 할 Mixin
 *
 * 주의) Watch를 mixin에 둘 경우 여러번 탐지되는 오동작 발생
 */
@Component
export default class DefaultPaginateMixin extends DefaultViewMixin {
    protected calculatedHeight: string = '';
    protected columns: TableHeader[] = [];
    protected pageInfo: PageInfo = {
        menuId: '',
        pageNo: 0,
        rowCount: 0,
        totalCount: 0,
        totalPage: 0
    };
    protected fullTextSupported: boolean = false;
    
    get isLoading() {
        return this.loading;
    }
    
    get getColumns() {
        return this.columns;
    }
    
    get getPageInfo() {
        return this.pageInfo;
    }

    get getPaginatedRowClassSupplier() {
        return (row: any) => {
            return {
                'delete-row': row.delFlag === 'Y' || row.deleted === true || row.personDeleteFlag === 'Y'
                    || row.DEL_FLAG === 'Y' || row.deleted,
            };
        };
    }

    get getAsyncParamKey(): (...params: string[]) => Promise<string> {
        return (keyName: string, redirectPage: string) =>
          CommonUtils.getParamKey(this.$route, this.$router, keyName)
                     .catch(() => {
                         this.$router
                             .push({name: redirectPage})
                             .then(() => {
                                 MessageUtils.clear();
                                 MessageUtils.displayErrorMessage(this.$t('message.error.unexpectedError'));
                             });
                         return '';
                     });
    }

    public mounted() {
        if (this.fullTextSupported) {
            this.$store.commit('onFullTextSupport');
            this.$store.commit('setFullText', false);
        }
    }
    
    public getNo(index: number) {
        return this.pageInfo.totalCount - ((this.pageInfo.pageNo - 1) * this.pageInfo.rowCount) - index;
    }
    /**
     * Filter 초기화 등으로 인해 리프레시가 필요할 때
     * 현재 리스트 메뉴에서 pageNo:1, rowCount:10으로 이동합니다.
     *
     * @param viewName VueRoute - View name
     * @param query query parameter
     */
    protected onRefresh(viewName: string, query?: any): Promise<any> | null {
        return this.$router.push({
            name: viewName,
            params: {
                ...query,
                pageNo: 1,
                hash: ApiUtils.getHash()
            }
        });
    }
    
    /**
     * 특정 행에 가해진 변경사항을 반영하기 위해
     * 현재 리스트 메뉴에서 pageNo, rowCount로 이동합니다.
     * @param viewName VueRoute - View name
     * @param pageNo pageNo
     * @param rowCount rowCount
     * @param query query parameter
     */
    protected onRefreshInCurrentPage(viewName: string, pageNo: string, rowCount: string, query?: any): Promise<any> | null {
        return this.$router.push({
            name: viewName,
            params: {
                ...query,
                pageNo,
                rowCount,
                hash: ApiUtils.getHash()
            }
        });
    }
    
    protected onNavigate(viewName: string, params: any): Promise<any> | null {
        return this.$router.push({
            name: viewName,
            params: {
                ...params
            }
        });
    }
    
    /**
     * Submit action이 성공 했을 때 공통으로 처리하는 핸들러
     *
     * - 화면에 띄워진 메시지를 모두 닫고 성공 메시지를 띄운다
     * - Store에 커밋 된 Change map을 비우며 loading spinner를 off 한다
     *
     * @param message Success message (default = 'message.success')
     * @see DefaultFormMixin#afterSubmitSuccess
     */
    protected afterSubmitSuccess(message: string = this.$t('message.success').toString()) {
        MessageUtils.clear();
        MessageUtils.displaySuccessMessage(message);
    
        return new Promise(resolve => {
            setTimeout(() => {
                this.loading = false;
                resolve();
            }, 300);
        });
    }
    
    protected destroyed() {
        if (this.fullTextSupported) {
            this.$store.commit('offFullTextSupport');
        }
    }
}
